import React from "react";

import { faExclamationTriangle } from "@fortawesome/pro-regular-svg-icons";
import { faPen } from "@fortawesome/pro-solid-svg-icons";
import axios from "axios";
import {
  Control,
  Controller,
  useForm,
  useFormState,
  useWatch,
} from "react-hook-form";

import { LifecycleSummary } from "src/adminPanel/modulesV2/LifecycleSummary";
import { getModuleKindDisplayName } from "src/adminPanel/modulesV2/utils";
import { Module, useCreateOperation } from "src/api/modulesApi";
import { Button } from "src/base-components/Button";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { MergeView } from "src/base-components/CodeInput/MergeView";
import { Input } from "src/base-components/Input";
import { PillToggle } from "src/base-components/PillToggle";
import { Select } from "src/base-components/Select";
import { ModuleKind } from "src/clients/flow-api";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { logger } from "src/utils/logger";

const deserializeConfigValueToString = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
};

const tryParseJson = (
  value: string,
): { valid: true; parsed: unknown } | { valid: false } => {
  try {
    return { valid: true, parsed: JSON.parse(value) };
  } catch {
    return { valid: false };
  }
};

const isObjectLikeOriginalValue = (originalValueString: string): boolean => {
  if (originalValueString === "") return false;
  const result = tryParseJson(originalValueString);
  return (
    result.valid && typeof result.parsed === "object" && result.parsed !== null
  );
};

const serializeConfigValueToJsonable = (
  stringValue: string,
  originalValue: unknown,
): unknown => {
  if (stringValue === "") {
    return "";
  }

  if (originalValue === null || originalValue === undefined) {
    return stringValue;
  }

  const originalType = typeof originalValue;

  if (originalType === "boolean") {
    if (stringValue.toLowerCase() === "true") {
      return true;
    }
    if (stringValue.toLowerCase() === "false") {
      return false;
    }

    throw new Error("Invalid value for boolean input");
  }

  if (originalType === "number") {
    const parsed = Number(stringValue);
    return isNaN(parsed) ? stringValue : parsed;
  }

  if (originalType === "object") {
    try {
      return JSON.parse(stringValue);
    } catch {
      return stringValue;
    }
  }

  return stringValue;
};

// An empty string is a legitimate value for a string-typed config field (e.g. a
// comma-delimited list cleared back to "no entries"). Number / boolean / object
// fields cannot be blanked — "" would not round-trip to their type — so only
// string-typed fields are allowed to become empty.
const allowsEmptyValue = (originalRawValue: unknown): boolean =>
  typeof originalRawValue === "string";

// Top-level spec fields hidden from the operation modal.
// is_active will be managed via a dedicated action in a follow-up.
const HIDDEN_SPEC_FIELDS = new Set(["is_active"]);

const PROFILE_LEVEL_OPTIONS = ["-1", "0", "1", "2", "3"].map((level) => ({
  key: level,
  value: level,
}));

type Props = {
  isOpen: boolean;
  onClose: () => void;
  afterLeave?: () => void;
  module: Module | undefined;
  workspaceName?: string;
  orgName?: string;
  onChangeLifecycle?: () => void;
};

type FormData = {
  version: string;
  profile_level: string;
  config: Record<string, string>;
};

type Change = {
  key: string;
  oldValue: string;
  newValue: string;
};

type DesiredSpecValues = {
  version: string;
  profile_level: number;
  config: Record<string, unknown>;
};

const getInputSuffixIcon = (isEmptyDirty: boolean, isChanged: boolean) => {
  if (isEmptyDirty) {
    return {
      icon: faExclamationTriangle,
      variant: "danger" as const,
    };
  }
  if (isChanged) {
    return {
      icon: faPen,
    };
  }
  return undefined;
};

const computeChangedFields = (
  desiredSpec: DesiredSpecValues,
  watchedVersion: string,
  watchedProfileLevel: string,
  watchedConfig: Record<string, string> | undefined,
): Change[] => {
  const changes: Change[] = [];

  if (watchedVersion !== desiredSpec.version) {
    changes.push({
      key: "version",
      oldValue: desiredSpec.version,
      newValue: watchedVersion,
    });
  }

  if (watchedProfileLevel !== String(desiredSpec.profile_level)) {
    changes.push({
      key: "profile_level",
      oldValue: String(desiredSpec.profile_level),
      newValue: watchedProfileLevel,
    });
  }

  const currentValues = watchedConfig ?? {};
  const configRecord = desiredSpec.config;

  Object.keys(currentValues)
    .map((key) => {
      const originalValue =
        configRecord?.[key] != null
          ? deserializeConfigValueToString(configRecord[key])
          : "";
      const newValue = currentValues[key] ?? "";
      return { key: `config.${key}`, oldValue: originalValue, newValue };
    })
    .filter((change) => change.newValue !== change.oldValue)
    .forEach((change) => changes.push(change));

  return changes;
};

// --- Sub-components ---

type SpecFieldsEditorProps = {
  control: Control<FormData>;
  currentVersion: string;
  currentProfileLevel: number;
};

const SpecFieldsEditor: React.FC<SpecFieldsEditorProps> = ({
  control,
  currentVersion,
  currentProfileLevel,
}) => {
  const version = useWatch({ name: "version", control });
  const hasEmptyDirtyVersionField = version === "" && currentVersion !== "";
  const isVersionChanged = version !== currentVersion;

  return (
    <div className="space-y-3">
      <div>
        <div className="mb-2 font-inter-medium-12px text-gray-500 uppercase tracking-wide">
          Version
        </div>
        <div className="rounded border bg-white p-3">
          <Controller
            control={control}
            name="version"
            render={({ field: controllerField }) => (
              <Tooltip
                disabled={!hasEmptyDirtyVersionField}
                title="Value cannot be empty"
                asChild
              >
                <Input
                  {...controllerField}
                  errored={hasEmptyDirtyVersionField}
                  placeholder="Enter version"
                  suffixIcon={getInputSuffixIcon(
                    hasEmptyDirtyVersionField,
                    isVersionChanged,
                  )}
                  fullWidth
                />
              </Tooltip>
            )}
          />
          <div className="mt-2 font-inter-normal-13px text-gray-600">
            <span className="text-gray-500">Current: </span>
            <code className="rounded border bg-gray-100 px-2 py-1 font-code-12">
              {currentVersion}
            </code>
          </div>
        </div>
      </div>

      {!HIDDEN_SPEC_FIELDS.has("profile_level") && (
        <div>
          <div className="mb-2 font-inter-medium-12px text-gray-500 uppercase tracking-wide">
            Profile Level
          </div>
          <div className="rounded border bg-white p-3">
            <Controller
              control={control}
              name="profile_level"
              render={({ field: controllerField }) => (
                <Select
                  options={PROFILE_LEVEL_OPTIONS}
                  value={controllerField.value}
                  onChange={(val) => controllerField.onChange(val)}
                />
              )}
            />
            <div className="mt-2 font-inter-normal-13px text-gray-600">
              <span className="text-gray-500">Current: </span>
              <code className="rounded border bg-gray-100 px-2 py-1 font-code-12">
                {String(currentProfileLevel)}
              </code>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

type ConfigParameterFieldProps = {
  configKey: string;
  originalValue: string;
  allowEmpty: boolean;
  control: Control<FormData>;
};

const ConfigParameterField: React.FC<ConfigParameterFieldProps> = React.memo(
  ({ configKey, originalValue, allowEmpty, control }) => {
    const value = useWatch({
      name: `config.${configKey}` as `config.${string}`,
      control,
    });
    const updatedValue = value ?? "";
    const isChanged = updatedValue !== originalValue;
    const isEmptyDirty = isChanged && updatedValue === "" && !allowEmpty;
    const useCodeEditor = isObjectLikeOriginalValue(originalValue);
    const hasInvalidJson =
      useCodeEditor && updatedValue !== "" && !tryParseJson(updatedValue).valid;
    const [viewMode, setViewMode] = React.useState<"editor" | "diff">("editor");

    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <label
            className="block font-inter-medium-13px text-gray-900"
            htmlFor={`config.${configKey}`}
          >
            <code className="rounded-md border px-2.5 py-1 font-code-13">
              {configKey}
            </code>
          </label>
          {useCodeEditor && (
            <PillToggle value={viewMode} onChange={setViewMode}>
              <PillToggle.Button key="editor" value="editor">
                Editor
              </PillToggle.Button>
              <PillToggle.Button key="diff" value="diff">
                Diff View
              </PillToggle.Button>
            </PillToggle>
          )}
        </div>
        <Controller
          control={control}
          name={`config.${configKey}` as const}
          render={({ field: controllerField }) =>
            useCodeEditor ? (
              <div>
                {viewMode === "editor" ? (
                  <div className="h-[40vh]">
                    <CodeEditor
                      drawErrorBorder={isEmptyDirty || hasInvalidJson}
                      language="json"
                      lineWrapping
                      placeholder={`Enter JSON value for ${configKey}`}
                      value={controllerField.value ?? ""}
                      onBlur={controllerField.onBlur}
                      onChange={controllerField.onChange}
                    />
                  </div>
                ) : (
                  <div className="h-[40vh] rounded-lg border bg-gray-50 p-1">
                    <MergeView
                      modified={updatedValue}
                      original={originalValue}
                    />
                  </div>
                )}
                {(isEmptyDirty || hasInvalidJson) && (
                  <ErrorHint>
                    {isEmptyDirty ? "Value cannot be empty" : "Invalid JSON"}
                  </ErrorHint>
                )}
              </div>
            ) : (
              <Tooltip
                disabled={!isEmptyDirty}
                title="Value cannot be empty"
                asChild
              >
                <Input
                  {...controllerField}
                  errored={isEmptyDirty}
                  placeholder={`Enter value for ${configKey}`}
                  suffixIcon={getInputSuffixIcon(isEmptyDirty, isChanged)}
                  fullWidth
                />
              </Tooltip>
            )
          }
        />
        {originalValue && !useCodeEditor && (
          <div className="mt-2 font-inter-normal-13px text-gray-600">
            <span className="text-gray-500">Current: </span>
            <code className="rounded border bg-gray-100 px-2 py-1 font-code-12">
              {originalValue}
            </code>
          </div>
        )}
      </div>
    );
  },
);

ConfigParameterField.displayName = "ConfigParameterField";

type ChangesSummaryProps = {
  changes: Change[];
};

const ChangesSummary: React.FC<ChangesSummaryProps> = ({ changes }) => (
  <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
    <h3 className="mb-3 font-inter-semibold-13px text-orange-900">
      Summary of Changes ({changes.length})
    </h3>
    <div className="space-y-2">
      {changes.map((change) => (
        <div key={change.key} className="font-inter-normal-13px">
          <code className="rounded bg-orange-100 px-2 py-1 font-code-12 text-orange-800">
            {change.key}
          </code>
          <div className="mt-1 ml-1 font-inter-normal-12px text-orange-700">
            <span className="text-gray-500 line-through">
              {change.oldValue || "(empty)"}
            </span>
            <span className="mx-2">&rarr;</span>
            <span className="font-medium">{change.newValue}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

type ChangesSummarySectionProps = {
  control: Control<FormData>;
  desiredSpec: DesiredSpecValues;
};

const ChangesSummarySection: React.FC<ChangesSummarySectionProps> = ({
  control,
  desiredSpec,
}) => {
  const watchedVersion = useWatch({ name: "version", control });
  const watchedProfileLevel = useWatch({ name: "profile_level", control });
  const watchedConfig = useWatch({ name: "config", control });

  const changedFields = computeChangedFields(
    desiredSpec,
    watchedVersion,
    watchedProfileLevel,
    watchedConfig,
  );

  if (changedFields.length === 0) return null;

  return <ChangesSummary changes={changedFields} />;
};

type FormFooterProps = {
  control: Control<FormData>;
  desiredSpec: DesiredSpecValues | undefined;
  isPending: boolean;
};

const FormFooter: React.FC<FormFooterProps> = ({
  control,
  desiredSpec,
  isPending,
}) => {
  const watchedVersion = useWatch({ name: "version", control });
  const watchedProfileLevel = useWatch({ name: "profile_level", control });
  const watchedConfig = useWatch({ name: "config", control });
  const { isSubmitting } = useFormState({ control });

  const changedFields = desiredSpec
    ? computeChangedFields(
        desiredSpec,
        watchedVersion,
        watchedProfileLevel,
        watchedConfig,
      )
    : [];
  const hasChanges = changedFields.length > 0;

  const hasEmptyDirtyVersionField =
    watchedVersion === "" && (desiredSpec?.version ?? "") !== "";

  const hasEmptyDirtyConfigFields = desiredSpec
    ? Object.keys(watchedConfig ?? {}).some((key) => {
        const rawOriginalValue = desiredSpec.config?.[key];
        const originalValue =
          rawOriginalValue != null
            ? deserializeConfigValueToString(rawOriginalValue)
            : "";
        const currentValue = watchedConfig?.[key] ?? "";
        return (
          currentValue === "" &&
          originalValue !== "" &&
          !allowsEmptyValue(rawOriginalValue)
        );
      })
    : false;

  const hasInvalidJsonConfigFields = desiredSpec
    ? Object.keys(watchedConfig ?? {}).some((key) => {
        const originalValue =
          desiredSpec.config?.[key] != null
            ? deserializeConfigValueToString(desiredSpec.config[key])
            : "";
        const currentValue = watchedConfig?.[key] ?? "";
        if (!isObjectLikeOriginalValue(originalValue)) return false;
        if (currentValue === "") return false;
        return !tryParseJson(currentValue).valid;
      })
    : false;

  return (
    <Modal.Footer
      primaryButton={
        <Button
          disabled={
            isSubmitting ||
            !hasChanges ||
            hasEmptyDirtyVersionField ||
            hasEmptyDirtyConfigFields ||
            hasInvalidJsonConfigFields ||
            isPending
          }
          htmlType="submit"
          loading={isSubmitting || isPending}
          variant="primary"
        >
          {isSubmitting || isPending
            ? "Creating..."
            : hasChanges
              ? `Create Operation (${changedFields.length} change${changedFields.length !== 1 ? "s" : ""})`
              : "No Changes"}
        </Button>
      }
      secondaryButton="Cancel"
    />
  );
};

// --- Main component ---

export const CreateOperationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  afterLeave,
  module,
  workspaceName,
  orgName,
  onChangeLifecycle,
}) => {
  const createOperation = useCreateOperation();

  const desiredSpec = module?.desiredSpec;
  const configKeys = desiredSpec ? Object.keys(desiredSpec.config) : [];
  const kindDisplay = desiredSpec
    ? getModuleKindDisplayName(desiredSpec.kind as ModuleKind)
    : "";

  const { handleSubmit, reset, control } = useForm<FormData>({
    values: desiredSpec
      ? {
          version: desiredSpec.version,
          profile_level: String(desiredSpec.profile_level),
          config: Object.entries(desiredSpec.config).reduce(
            (acc, [key, value]) => {
              acc[key] = deserializeConfigValueToString(value);
              return acc;
            },
            {} as Record<string, string>,
          ),
        }
      : {
          version: "",
          profile_level: "0",
          config: {},
        },
  });

  const onSubmit = async (data: FormData) => {
    if (!module || !desiredSpec) return;

    try {
      const patch: Record<string, unknown> = {};

      if (data.version !== desiredSpec.version) {
        patch.version = data.version;
      }

      if (data.profile_level !== String(desiredSpec.profile_level)) {
        const parsed = Number(data.profile_level);
        patch.profile_level = isNaN(parsed) ? data.profile_level : parsed;
      }

      const configRecord = desiredSpec.config as Record<string, unknown>;
      const configPatch: Record<string, unknown> = {};

      Object.keys(data.config).forEach((key) => {
        const stringValue = data.config[key] ?? "";
        const originalStr =
          configRecord?.[key] != null
            ? deserializeConfigValueToString(configRecord[key])
            : "";
        if (stringValue !== originalStr) {
          configPatch[key] = serializeConfigValueToJsonable(
            stringValue,
            configRecord?.[key],
          );
        }
      });

      if (Object.keys(configPatch).length > 0) {
        patch.config = configPatch;
      }

      await createOperation.mutateAsync({
        data: {
          type: "module_operations",
          attributes: { patch },
          relationships: {
            module: {
              data: { type: "modules", id: module.id },
            },
          },
        },
      });

      toastActions.success({
        title: "Operation created",
        description: `A new operation has been created for module ${module.id.slice(0, 8)}`,
      });
      handleClose();
    } catch (error) {
      const rawErrorMessage =
        axios.isAxiosError(error) && error.response?.data?.detail
          ? error.response.data.detail
          : error instanceof Error
            ? error.message
            : error;

      const errorDescription =
        typeof rawErrorMessage === "string"
          ? rawErrorMessage
          : JSON.stringify(rawErrorMessage);

      logger.error(`Error creating operation: ${error}`);
      toastActions.failure({
        title: "Failed to create operation",
        description: errorDescription,
      });
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      afterLeave={afterLeave}
      open={isOpen}
      size="lg"
      onClose={handleClose}
    >
      <Modal.Header>
        Create Operation
        {module && (
          <p className="mt-2 font-inter-normal-13px text-gray-600">
            Create a new module operation for{" "}
            <strong>{workspaceName ?? module.workspaceId.slice(0, 8)}</strong>
          </p>
        )}
      </Modal.Header>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Content>
          <div className="space-y-6">
            {module && desiredSpec && (
              <div className="rounded-lg border bg-gray-50 p-4">
                <h3 className="mb-4 font-inter-semibold-13px text-gray-900">
                  Module Details
                </h3>
                <div className="grid grid-cols-2 gap-4 font-inter-normal-13px">
                  <div className="space-y-3">
                    {orgName && (
                      <div>
                        <div className="font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                          Organization
                        </div>
                        <span className="font-inter-normal-13px text-gray-900">
                          {orgName}
                        </span>
                      </div>
                    )}
                    {workspaceName && (
                      <div>
                        <div className="font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                          Workspace
                        </div>
                        <span className="font-inter-normal-13px text-gray-900">
                          {workspaceName}
                        </span>
                      </div>
                    )}
                    <div>
                      <div className="font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                        Module ID
                      </div>
                      <code className="font-code-12 text-gray-700">
                        {module.id}
                      </code>
                    </div>
                    <div>
                      <div className="font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                        Kind
                      </div>
                      <span className="font-inter-normal-13px text-gray-900">
                        {kindDisplay}
                      </span>
                    </div>
                  </div>

                  <SpecFieldsEditor
                    control={control}
                    currentProfileLevel={desiredSpec.profile_level}
                    currentVersion={desiredSpec.version}
                  />
                </div>
              </div>
            )}

            {module && (
              <LifecycleSummary module={module} onChange={onChangeLifecycle} />
            )}

            {desiredSpec && configKeys.length > 0 && (
              <div className="space-y-5">
                <div className="border-gray-200 border-b pb-2">
                  <h3 className="font-inter-semibold-16px text-gray-900">
                    Configuration Parameters
                  </h3>
                  <p className="mt-1 font-inter-normal-13px text-gray-500">
                    Only changed fields will be included in the operation patch
                  </p>
                </div>

                {configKeys.map((key) => {
                  const rawValue = (
                    desiredSpec.config as Record<string, unknown>
                  )[key];
                  const originalValue = deserializeConfigValueToString(
                    rawValue ?? "",
                  );

                  return (
                    <ConfigParameterField
                      key={key}
                      allowEmpty={allowsEmptyValue(rawValue)}
                      configKey={key}
                      control={control}
                      originalValue={originalValue}
                    />
                  );
                })}
              </div>
            )}

            {desiredSpec && configKeys.length === 0 && (
              <div className="py-8 text-center text-gray-500">
                <p>No configuration parameters found for this module.</p>
              </div>
            )}

            {desiredSpec && (
              <ChangesSummarySection
                control={control}
                desiredSpec={desiredSpec}
              />
            )}
          </div>
        </Modal.Content>

        <FormFooter
          control={control}
          desiredSpec={desiredSpec}
          isPending={createOperation.isPending}
        />
      </form>
    </Modal>
  );
};
